import { collection, doc, getDoc, getDocs } from "firebase/firestore/lite";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { db } from "../auth/firebase";
import { useGlobalState } from "../globalState/GlobalState";
import type { Project } from "../types";

async function assetExists(url?: string, signal?: AbortSignal) {
  if (!url) return false;
  try {
    const res = await fetch(url, { method: "HEAD", signal });
    return res.ok;
  } catch {
    return false;
  }
}

const getSizes = async (
  url?: string,
  fileName?: string,
  subfolder?: string,
  sizes?: number[],
  signal?: AbortSignal,
) => {
  if (!url || !fileName || !subfolder || !sizes)
    return await Promise.resolve([]);
  return Promise.all(
    sizes.map(async (size) => {
      const newUrl = url?.replace(
        fileName,
        `${subfolder}%2F${fileName}_${size}x${size}`,
      );
      if (!(await assetExists(newUrl, signal))) return;
      return {
        size: size / 2,
        url: newUrl,
      };
    }),
  );
};

const fireStoreSizesCache = new Map<string, { size: number; url: string }[]>();
const fireStoreSizesInFlight = new Map<
  string,
  Promise<{ size: number; url: string }[]>
>();

function pickProbeSizes(allSizes: number[], placeholderSize: number) {
  // Probing every size causes many network requests in the builder.
  // Limit to a small representative set; the original URL is still used as `src`.
  const preferred = [placeholderSize, 320, 640, 1024, 1280, 1920];
  const allowed = new Set(allSizes);
  const picked = preferred.filter((s) => allowed.has(s));
  // Ensure placeholder is included if it exists in list.
  if (allowed.has(placeholderSize) && !picked.includes(placeholderSize)) {
    picked.unshift(placeholderSize);
  }
  // Fall back to first few sizes if lists are unusual.
  const fallback = allSizes.slice(0, 4);
  return Array.from(new Set([...(picked.length ? picked : fallback)])).sort(
    (a, b) => a - b,
  );
}

export const useGetFireStoreSizes = (
  url?: string,
  placeholderSize = 128,
  sizes = [32, 64, 128, 320, 640, 768, 1024, 1280, 1536, 1920, 2560],
  subfolder = "resized",
) => {
  const [error, setError] = useState(false);

  const fileName = useMemo(() => {
    if (!url) return "";
    return decodeURIComponent(new URL(url).pathname).split("/").pop() || "";
  }, [url]);

  const [sources, setSources] = useState<{ size: number; url: string }[]>([]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!fileName) return;
    const key = `${url}::${subfolder}::${placeholderSize}`;
    const cached = fireStoreSizesCache.get(key);
    if (cached) {
      setSources(cached);
      return;
    }

    const controller = new AbortController();
    const probeSizes = pickProbeSizes(sizes, placeholderSize);

    const existingInFlight = fireStoreSizesInFlight.get(key);
    if (existingInFlight) {
      existingInFlight
        .then((e) => {
          fireStoreSizesCache.set(key, e);
          setSources(e);
        })
        .catch(() => {});
      return () => controller.abort();
    }

    const promise = getSizes(
      url,
      fileName,
      subfolder,
      probeSizes,
      controller.signal,
    )
      .then((e) =>
        e.filter((x): x is { size: number; url: string } => x !== undefined),
      )
      .then((e) => {
        fireStoreSizesCache.set(key, e);
        return e;
      })
      .finally(() => {
        fireStoreSizesInFlight.delete(key);
      });

    fireStoreSizesInFlight.set(key, promise);
    promise.then(setSources).catch(() => {});

    return () => controller.abort();
  }, [url, fileName, subfolder]);

  const srcSet = useMemo(() => {
    return sources.map(({ url, size }) => `${url} ${size}w`).join(", ");
  }, [sources]);

  useEffect(() => {
    if (!sources.length || !sources[0].url) return;

    const image = new Image();
    image.onload = () => setError(false);
    image.onerror = () => setError(true);
    image.src = sources[0].url;

    return () => {
      image.onload = null;
      image.onerror = null;
    };
  }, [sources]);

  if (!url)
    return {
      placeholderSrc: "",
      src: "",
      srcSet: "",
      sizes: "",
      error: true,
    };

  return {
    placeholderSrc:
      sources.find((img) => img.size === placeholderSize)?.url || url,
    src: url,
    srcSet: error ? "" : srcSet,
    error,
  };
};

export const useGetTranslations = (project: Project) => {
  const [_, setTranslation] = useGlobalState().translationsState;

  const checkProjectHasTranslations = useCallback(async () => {
    if (!project?.organizationId || !project?.projectId) return false;
    // translations is a collection, so we need to check if it has any documents
    const translationCollectionPath = `organizations/${project.organizationId}/projects/${project.projectId}/translations`;
    // Use getDocs to check if there is at least one document in the collection
    const translationCollectionRef = collection(db, translationCollectionPath);
    const snapshot = await getDocs(translationCollectionRef);
    return !snapshot.empty;
  }, [project?.organizationId, project?.projectId]);

  const fetchTranslations = async (lang: string) => {
    if (!project?.organizationId || !project?.projectId) return;
    const translationJsonPath = `organizations/${project?.organizationId}/projects/${project?.projectId}/translations/${lang}`;
    const translationJsonDoc = await getDoc(doc(db, translationJsonPath));
    if (!translationJsonDoc.exists()) return;
    const translationJson = translationJsonDoc.data();

    const { translations, originalToHashedMap } = translationJson;
    const unhashedTranslations = {};

    // List of keys to filter out when language ends with -simple
    // const keysToFilter = [
    //   project?.title,
    //   // Add all titles from project.content
    //   ...(project?.content?.map(item => item.title) || [])
    // ].filter(Boolean);

    for (const [hashedKey, value] of Object.entries(translations)) {
      const originalKey = originalToHashedMap?.[hashedKey] || hashedKey;
      // Skip if language ends with -simple and key is in the filter list
      // if (lang.endsWith("-simple") && keysToFilter.includes(originalKey)) {
      //   continue;
      // }
      unhashedTranslations[originalKey] = value;
    }
    setTranslation(unhashedTranslations);
  };

  return { fetchTranslations, checkProjectHasTranslations };
};

export const useDimensions = <T extends HTMLElement>(
  ref?: React.RefObject<T>,
) => {
  const fallbackRef = useRef<T>(null);
  const containerRef = ref || fallbackRef;
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    const element = containerRef.current;

    if (element) {
      // Create ResizeObserver
      resizeObserverRef.current = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setDimensions({
            width: entry.contentRect.width,
            height: entry.contentRect.height,
          });
        }
      });

      resizeObserverRef.current.observe(element);

      // Set initial dimensions
      setDimensions({
        width: element.offsetWidth,
        height: element.offsetHeight,
      });
    }

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [containerRef.current]);

  return [containerRef, dimensions] as const;
};
const quotes = {
  ar: ["«", "»"], // Arabic
  cs: ["„", "“"], // Czech
  de: ["„", "“"], // German
  en: ["“", "”"], // English
  es: ["«", "»"], // Spanish
  fr: ["«\u00A0", "\u00A0»"], // French with non-breaking space
  it: ["«", "»"], // Italian
  nl: ["“", "”"], // Dutch (typically same as English)
  pl: ["„", "”"], // Polish
  ru: ["«", "»"], // Russian
  tr: ["“", "”"], // Turkish
  uk: ["«", "»"], // Ukrainian
};
export const useLocalizeQuotes = (locale?: string) => {
  const { i18n } = useTranslation();
  const localString = locale || i18n.resolvedLanguage;
  const localizedQuote = useCallback(
    (text: string) => {
      const [open, close] = quotes[localString as keyof typeof quotes] || [
        '"',
        '"',
      ];
      return `${open}${text}${close}`;
    },
    [localString],
  );

  return localizedQuote;
};

export const useIsTruncated = <T extends HTMLElement | null>(
  ref?: React.RefObject<T>,
  enabled = true,
) => {
  const fallbackRef = useRef<T>(null);
  const containerRef = ref || fallbackRef;
  const [isTruncated, setIsTruncated] = useState(false);

  // When disabled, skip ResizeObserver so we don't cause rerenders on layout changes (e.g. scroll)
  // biome-ignore lint/correctness/useExhaustiveDependencies: Effect runs when enabled changes; ref and ResizeObserver handle updates
  useEffect(() => {
    if (!enabled || !containerRef.current) return;

    const checkTruncation = () => {
      if (containerRef.current) {
        const truncated =
          containerRef.current.scrollHeight >
          containerRef.current.clientHeight + 5;
        setIsTruncated((prev) => (prev !== truncated ? truncated : prev));
      }
    };

    // Check immediately
    checkTruncation();

    // Check after a short delay to ensure content is rendered
    const timeoutId = setTimeout(checkTruncation, 10);

    // Set up ResizeObserver to detect container size changes
    const resizeObserver = new ResizeObserver(() => {
      checkTruncation();
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [enabled]);
  return [containerRef, enabled ? isTruncated : false] as const;
};
